import React, { useState, useEffect, useRef } from 'react';
import JSONCompareViewer, { JSONCompareViewerRef } from '../../../Components/JSONCompareViewer';
import InputField from '../../../Components/InputField';
import TextAreaField from '../../../Components/TextAreaField';
import ToastManager from '../../../Components/ToastManager';
import { NetworkManager } from '../../../Components/NetworkManager';

interface InvoiceDetailViewerProps {
    groupDetails: any;
    groupCode: string;
    onRefresh: (groupCode: string) => void;
    onViewHistory: (activeTab: string, id: any) => void;
    onClose?: () => void;
}

const InvoiceDetailViewer: React.FC<InvoiceDetailViewerProps> = ({
    groupDetails,
    groupCode,
    onRefresh,
    onViewHistory,
    onClose
}) => {
    const [activeTab, setActiveTab] = useState('');
    const [itemComments, setItemComments] = useState<any>({});
    const [itemPayloads, setItemPayloads] = useState<any>({});
    const [expandedItems, setExpandedItems] = useState<any>({});
    const [viewerStates, setViewerStates] = useState<any>({});
    const viewerRefs = useRef<{[key: string]: JSONCompareViewerRef | null}>({});

    useEffect(() => {
        if (groupDetails && !activeTab) {
            const initialExpanded: any = {};
            let firstTab: string = '';
            
            if (groupDetails.invoices?.length > 0) {
                firstTab = 'invoice';
            } else if (groupDetails.statements?.length > 0) {
                firstTab = 'statement';
            } else if (groupDetails.payments?.length > 0) {
                firstTab = 'payment';
            }

            if (firstTab) {
                setActiveTab(firstTab);
                const items = groupDetails[firstTab === 'invoice' ? 'invoices' : firstTab === 'statement' ? 'statements' : 'payments'];
                if (items?.length > 0) {
                    const itemToExpand = items.length === 1 
                        ? items[0] 
                        : (items.find((i: any) => !i.sentToPrextra) || items[0]);
                    const id = itemToExpand.id || itemToExpand.invoiceId || itemToExpand.documentId;
                    initialExpanded[id] = true;
                }
            }

            setExpandedItems(initialExpanded);
        }
    }, [groupDetails, activeTab]);

    const handlePayloadChange = (id: any, newData: any) => {
        setItemPayloads((prev: any) => ({ ...prev, [id]: newData }));
    };

    const handleCommentChange = (id: any, value: any) => {
        setItemComments((prev: any) => ({ ...prev, [id]: value }));
    };

    const toggleExpand = (id: any) => {
        setExpandedItems((prev: any) => {
            const isCurrentlyExpanded = !!prev[id];
            // Clear all and toggle only the target one
            return { [id]: !isCurrentlyExpanded };
        });
    };

    const handleSaveItem = (item: any, sendToPrextra: boolean) => {
        const itemId = item.id || item.invoiceId || item.documentId;
        const payload = {
            id: itemId,
            payload: itemPayloads[itemId] || item.payload,
            comment: itemComments[itemId] || "",
            sendToPrextra: sendToPrextra
        };

        NetworkManager.sendJsonRequest('api/prextra/review/save', payload, '', 'POST', true, (res: any) => {
            if (res) {
                const response = JSON.parse(res);
                if (!response.errorMessage) {
                    ToastManager.showToast("Saved Successfully", 'Success');
                    
                    const checkCanClose = () => {
                        const allInvoicesSent = groupDetails.invoices?.every((i: any) => {
                            const currentId = i.id || i.invoiceId || i.documentId;
                            return currentId === itemId ? sendToPrextra : i.sentToPrextra;
                        }) ?? true;
                        
                        const allStatementsSent = groupDetails.statements?.every((s: any) => {
                            const currentId = s.id || s.invoiceId || s.documentId;
                            return currentId === itemId ? sendToPrextra : s.sentToPrextra;
                        }) ?? true;
                        
                        const allPaymentsSent = groupDetails.payments?.every((p: any) => {
                            const currentId = p.id || p.invoiceId || p.documentId;
                            return currentId === itemId ? sendToPrextra : p.sentToPrextra;
                        }) ?? true;
                        
                        return allInvoicesSent && allStatementsSent && allPaymentsSent;
                    };

                    if (sendToPrextra && checkCanClose() && onClose) {
                        onClose();
                    } else {
                        onRefresh(groupCode);
                    }
                } else {
                    ToastManager.showToast(response.errorMessage, 'Error');
                }
            }
        }, (err: any) => {
            console.error(err);
        });
    };

    const getStatusIcon = (isFlagged: boolean, isSent: boolean, className: string = "ms-1") => {
        if (isFlagged) return <i className={`fa-solid fa-circle-info text-danger ${className}`} title="Flagged Issues"></i>;
        if (isSent) return <i className={`fa-solid fa-circle-check text-success ${className}`} title="Sent to Prextra"></i>;
        return <i className={`fa-solid fa-circle-exclamation text-warning ${className}`} title="Pending"></i>;
    };

    const renderItem = (item: any, type: string) => {
        const itemId = item.id || item.invoiceId || item.documentId;
        const isExpanded = !!expandedItems[itemId];
        const isFlagged = item.isFlagged;
        const isSent = item.sentToPrextra;
        const vState = viewerStates[itemId] || { isPdfLoading: false, hasPdf: false, isFullscreen: false };

        return (
            <div className="card mb-3 border-1 shadow-sm" key={`${type}-${itemId}`} style={{ width: '100%', overflow: 'hidden' }}>
                <div 
                    className="card-header d-flex justify-content-between align-items-center py-2 bg-light" 
                    style={{ 
                        cursor: 'pointer', 
                        borderLeft: isFlagged ? '5px solid #dc3545' : isSent ? '5px solid #198754' : '5px solid #ffc107',
                        transition: 'background-color 0.2s'
                    }}
                    onClick={() => toggleExpand(itemId)}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                >
                    <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center justify-content-center" style={{ width: '20px' }}>
                            <i className={`fa-solid ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} text-primary`}></i>
                        </div>
                        <h6 className="mb-0 fw-bold">
                            {type.charAt(0).toUpperCase() + type.slice(1)} - {item.invoiceCode}
                        </h6>
                        {getStatusIcon(isFlagged, isSent)}
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        {type === 'payment' && item.totalAmount ? <span className="fw-bold text-dark px-2 py-1 bg-white border rounded small">Amount: {item.totalAmount}</span> : null}
                        
                        {isExpanded && type !== 'payment' && (
                            <div className="d-flex align-items-center gap-2 border-end pe-3 me-2">
                                {vState.isPdfLoading ? (
                                    <i className="fa-solid fa-spinner fa-spin text-primary"></i>
                                ) : (
                                    <i 
                                        className={`fa-solid ${vState.hasPdf ? 'fa-file-pdf text-danger' : 'fa-file-lines text-secondary'} fs-6`} 
                                        onClick={(e) => { e.stopPropagation(); viewerRefs.current[itemId]?.togglePDF(); }}
                                        title={vState.hasPdf ? "Hide PDF Preview" : "Show PDF Preview"}
                                        style={{ cursor: 'pointer' }}
                                    ></i>
                                )}
                                <i 
                                    className="fa-solid fa-download text-primary fs-6 mx-3" 
                                    onClick={(e) => { e.stopPropagation(); viewerRefs.current[itemId]?.downloadPDF(); }}
                                    title="Download PDF"
                                    style={{ cursor: 'pointer' }}
                                ></i>
                                <i 
                                    className={`fa-solid ${vState.isFullscreen ? 'fa-compress' : 'fa-expand'} text-secondary fs-6 me-1`} 
                                    onClick={(e) => { e.stopPropagation(); viewerRefs.current[itemId]?.toggleFullscreen(); }}
                                    title={vState.isFullscreen ? "Exit Fullscreen" : "Maximize View"}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </div>
                        )}
                        <button 
                            className="btn btn-sm btn-outline-primary" 
                            onClick={(e) => { e.stopPropagation(); onViewHistory(type, itemId); }}
                        >
                            <i className="fa-solid fa-history me-1"></i> View history
                        </button>
                    </div>
                </div>
                {isExpanded && (
                    <div className="d-flex flex-column" style={{ height: '700px' }}>
                        <div className="card-body p-3 flex-grow-1" style={{ backgroundColor: '#fff', overflow: 'hidden' }}>
                            <JSONCompareViewer
                                ref={el => viewerRefs.current[itemId] = el}
                                data={item.payload}
                                title={`${type.charAt(0).toUpperCase() + type.slice(1)} - ${item.invoiceCode}`}
                                documentId={item?.documentId}
                                documentType={type}
                                height="100%"
                                onDataChange={(newData) => handlePayloadChange(itemId, newData)}
                                allowAdd={false}
                                allowDelete={false}
                                allowCopy={true}
                                flaggedComments={item.flaggedComments || []}
                                showExternalControls={true}
                                onStateChange={(state) => setViewerStates((prev: any) => ({ ...prev, [itemId]: state }))}
                            />
                        </div>
                        {!isSent && 
                            (<div className="card-footer bg-light p-3 border-top d-flex align-items-center gap-3">
                                <div className="flex-grow-1">
                                    <TextAreaField
                                        style={{ height: '50px', cursor: isSent ? 'not-allowed' : 'pointer' }}
                                        value={itemComments[itemId] || ''}
                                        onChange={(e: any) => handleCommentChange(itemId, e.target.value)}
                                        placeholder="Add Comment"
                                        disabled={isSent}
                                    />
                                </div>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-primary px-4"
                                        disabled={isSent || (itemComments[itemId] && itemComments[itemId].trim().length > 0 && itemComments[itemId].trim().length <= 3)}
                                        onClick={() => handleSaveItem(item, false)}
                                    >
                                        Save
                                    </button>
                                    <button
                                        className="btn btn-success px-4"
                                        disabled={isSent || (itemComments[itemId] && itemComments[itemId].trim().length > 0) || isFlagged}
                                        onClick={() => handleSaveItem(item, true)}
                                    >
                                        Send to Prextra
                                    </button>
                                </div>
                            </div>)}
                    </div>
                )}
            </div>
        );
    };

    const getTabStatusIcon = (items: any[]) => {
        if (!items || items.length === 0) return null;
        const isFlagged = items.some((i: any) => i.isFlagged);
        const allSent = items.every((i: any) => i.sentToPrextra);
        return getStatusIcon(isFlagged, allSent, "me-1");
    };

    return (
        <div className="d-flex flex-column" style={{ height: 'calc(100vh - 70px)' }}>
            <div className="d-flex justify-content-between align-items-center border-bottom ps-3 mb-2 pt-2">
                <ul className="nav nav-tabs mx-2" style={{ marginBottom: "-1px" }}>
                    {groupDetails?.invoices?.length > 0 && (
                        <li className="nav-item" style={{ cursor: 'pointer' }}>
                            <a className={`nav-link ${activeTab === 'invoice' ? 'active' : ''}`} onClick={() => setActiveTab('invoice')}>
                                {getTabStatusIcon(groupDetails.invoices)}
                                Invoices ({groupDetails.invoices.length})
                            </a>
                        </li>
                    )}
                    {groupDetails?.statements?.length > 0 && (
                        <li className="nav-item" style={{ cursor: 'pointer' }}>
                            <a className={`nav-link ${activeTab === 'statement' ? 'active' : ''}`} onClick={() => setActiveTab('statement')}>
                                {getTabStatusIcon(groupDetails.statements)}
                                Statements ({groupDetails.statements.length})
                            </a>
                        </li>
                    )}
                    {groupDetails?.payments?.length > 0 && (
                        <li className="nav-item" style={{ cursor: 'pointer' }}>
                            <a className={`nav-link ${activeTab === 'payment' ? 'active' : ''}`} onClick={() => setActiveTab('payment')}>
                                {getTabStatusIcon(groupDetails.payments)}
                                Payments ({groupDetails.payments.length})
                            </a>
                        </li>
                    )}
                </ul>
            </div>
            <div className="tab-content mx-3 flex-grow-1 bg-red" style={{ overflowY: 'auto', overflowX: 'hidden', paddingRight: '5px' }}>
                {activeTab === 'invoice' && (
                    <div className="">
                        {groupDetails?.invoices?.map((item: any) => renderItem(item, 'invoice'))}
                    </div>
                )}
                {activeTab === 'statement' && (
                    <div className="">
                        {groupDetails?.statements?.map((item: any) => renderItem(item, 'statement'))}
                    </div>
                )}
                {activeTab === 'payment' && (
                    <div className="">
                        {groupDetails?.payments?.map((item: any) => renderItem(item, 'payment'))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default InvoiceDetailViewer;
